import App from "app";
import dialog from "app/components/common/dialogs/dialogWindow";
import StreamDataSource from "app/components/common/editor/control/select/transformation-inputstream-datasource";
import UIControl from "app/components/common/editor/control/ui-control";
import BaseEditor from "app/components/common/editor/meta-object-editors/base";
import cqTransformationProvider from "app/components/common/editor/meta-object-editors/cqTransformations/transformationProvider";
import componentService from "../../../../../core/services/componentService/component-service";
import TqlBuilder from "app/components/common/tqlBuilder";
import { filterDbSources, loadAscendantsSources } from "app/components/flow/designer/component-acendants-loader";
import predefinedTransformationComponents from "app/components/flow/designer/predefinedComponents/predefinedTransformationComponents";
import api from "core/api/api";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import metaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import _ from "underscore";

export interface ITransformationConfiguration {
    convertToCQ: () => void;
    OnlyDbSources?: boolean;
    allowChangeSource?: boolean;
    createColumnControl?: () => void;
    buildTql?: (tqlBuilder: any, columns: any[], options: any) => void;
    postProcessing?: (cqControl: any, options: any, metadata_object: any) => void;
    createRqlBuilder?: (stream: string, columns: any[], options: any, transformation: any) => any;
    postRender?: (cqControl: any) => void;
}

function getSubtypeName(metaObjectModel) {
    if (metaObjectModel.uiconfig && metaObjectModel.uiconfig.subType) {
        return metaObjectModel.uiconfig.subType;
    }

    return "";
}

var Module = function(metadata_object, options) {
    options = options || {};
    this._options = options;
    options.displayFields = options.displayFields || ["select", "lb1", "output"];
    this.transformation = cqTransformationProvider.getTransformationByName(getSubtypeName(metadata_object));
    options.labels = {
        valuefield: {
            name: "Value Field",
            description:
                "Select the field in the input stream over which you want to run forecasting / anomaly detection. The data type of this field should be DOUBLE."
        },
        timestampfield: {
            name: "Timestamp Field",
            description:
                "Select the timestamp field in the input stream. The data type of this field should be DATETIME. This field denotes the time stamp at which the time series data value was observed. A default value of null can be used if there is no timestamp associated with the value."
        },
        numstepstopredict: {
            name: "Number of predicted future values",
            description: "The number of future values to be predicted, based on past data values."
        },
        intervalseconds: {
            name: "Interval Seconds",
            description:
                "This is the interval between successive time-stamps in the stream, in seconds. For example, if your stream contains one data point for every minute, this value will be 60."
        },
        algorithmoption: {
            name: "Algorithm Option",
            description: "Select the forecasting algorithm that you want to use."
        },
        enableseasonality: {
            name: "Enable Seasonality",
            description:
                "Select this option if you want to take seasonality of the time-series data into account during prediction."
        },
        seasonallength: {
            name: "Seasonal Length",
            description:
                "After how many points does the pattern in your data repeat? For example, if your time-series interval is 60 seconds (one data point every minute), and your data seasonality is daily, then you need to set this value to 1440 (minutes in a day). "
        },
        detectanomaly: {
            name: "Detect Anomaly",
            description: "Select this option if, along with forecasting, you want to detect anomalies as well."
        },
        anomalyepsilon: {
            name: "Anomaly Epsilon",
            description:
                "Choose how sensitive the model should be to outliers. Higher values will result in more points being marked as anomalies (less forgiving)."
        },
        anomalystart: {
            name: "Anomaly Start",
            description:
                "Do not detect anomalies for the first N data points. This gives the algorithm time to warm up to the distribution. The default value is 0, which means we detect anomalies for all data points."
        },
        retrainfrequency: {
            name: "Retrain Frequency",
            description:
                "Determines how often the model is retrained. For example, if it is set to 1, the model will be retrained every time it receives the next data point (most accurate but computationally intensive), and if it is set to 10, the model will be retrained after 10 new data points have been received (less accurate, but faster). "
        },
        missingvalueoption: {
            name: "Missing Value Option",
            description:
                "Choose how to handle missing values - “ignore” will ignore the missing values, and “zero” will fill the missing values as zeros."
        }
    };
    if (this.transformation) {
        options.displayFields.unshift("convertToCQ");
        options.displayFields.unshift("columns");
        if (metadata_object.uiconfig.subType === componentService.getHumanReadableName("TransformationFieldAdder")) {
            options.displayFields.unshift("header");
            options.displayFields.unshift("originalFields");
            options.displayFields.unshift("originalFieldsHeader");
        }

        options.displayFields.unshift("input");
        const isWAE = predefinedTransformationComponents.isWAEventTypeTransformationRelated(
            metadata_object.uiconfig.subType
        );
        const streams = new StreamDataSource(isWAE);
        this.input = UIControl.Select(streams).create();
        if (metadata_object.uiconfig.config && metadata_object.uiconfig.config.stream) {
            this.input.setValue(metadata_object.uiconfig.config.stream);
        }

        this.originalFieldsHeader = UIControl.Header("Existing fields").create();

        this.originalFields = UIControl.TypeViewer.extend({
            fullWidth: true,
            collapsed: true,
            hideLabel: true
        }).create();

        this.header = UIControl.Header("New fields").create();

        this.input.on(
            UIControl.EVENT.VALUE_CHANGED,
            function(value) {
                this.input.clearError();
                this.columns.setValue([]);

                if (!value) {
                    var columnsPickerRow = this.getControlRow(this.columns);
                    columnsPickerRow.hide();
                }

                loadAscendantsSources(value).then(sources => {
                    if (this.transformation.OnlyDbSources) {
                        let dbSources = filterDbSources(sources);

                        if (dbSources.length === 0) {
                            this.input.showError("Component supports only DB sources.");
                            this.toggleColumnsList(null);
                            return;
                        } else if (sources.length > 1) {
                            this.input.showError("Only one source is supported.");
                            this.toggleColumnsList(null);
                            return;
                        }
                    }

                    metadata_object.uiconfig.config.stream = value;
                    this.toggleColumnsList(value);
                    if (this.columns.clearColumnsOnStreamChanged) {
                        this.columns.clearColumnsDataSource();
                    }
                    this.fieldMapper.select.setValue("");
                    this.originalFields.viewTypeOf(value);
                });
            }.bind(this)
        );

        this.convertToCQ = UIControl.Button("Convert to CQ").create();
        this.convertToCQ.on(
            "clicked",
            function() {
                const message = "Are you sure you want to convert to CQ? This is a one-way process.";
                dialog.confirm(message, App.Dashboards.ModalManager).then(result => {
                    if (result) {
                        try {
                            this.Form.model.isDirty = true;
                            metadata_object.uiconfig = null;
                            this.getControlRow(this.input).hide();
                            this.getControlRow(this.columns).hide();
                            this.getControlRow(this.originalFieldsHeader).hide();
                            this.getControlRow(this.originalFields).hide();
                            this.getControlRow(this.header).hide();
                            this.getControlRow(this.convertToCQ).hide();

                            this.transformation?.convertToCQ?.apply(this);

                            this.fieldMapper.select.readOnly = false;
                            this.fieldMapper.select.setViewEnabled();
                            this.options.StaticControls.title.setType(metaStoreService.entities.CQ, "cq");
                        } catch (e) {
                            console.log(e);
                        }
                    }
                });
            }.bind(this)
        );

        this.columns = this.transformation.createColumnControl();
        this.columns.setValue(metadata_object.uiconfig.config.columns);
        this.columns.on(
            UIControl.EVENT.VALUE_CHANGED,
            function(value) {
                metadata_object.uiconfig.config.columns = value;
                this.refreshTql(value);
            }.bind(this)
        );

        this.columns.on(
            "show-error-msg",
            function(message) {
                this.input.showError(message);
                this.toggleColumnsList(null);
            }.bind(this)
        );

        options.StaticControls = {
            input: this.input,
            originalFieldsHeader: this.originalFieldsHeader,
            originalFields: this.originalFields,
            header: this.header,
            columns: this.columns,
            convertToCQ: this.convertToCQ
        };
        try {
            if (this?.transformation?.postProcessing) {
                this.transformation.postProcessing(this, options, metadata_object);
            }
        } catch (e) {
            console.log(e);
        }
    }

    BaseEditor.apply(this, arguments);
};

Module.prototype = _.extend({}, BaseEditor.prototype, {
    getControlRow: function(control) {
        return control.$el.closest(".field-row");
    },

    toggleColumnsList: async function(value) {
        var columnsPickerRow = this.getControlRow(this.columns);
        if (!value) {
            columnsPickerRow.hide();
        } else {
            columnsPickerRow.show();
            await this.columns.setStream(value);
            if (this.transformation.allowChangeSource !== true) {
                this.input.setEnabled(false);
            }
        }
    },

    addCustomFields: function() {
        this.fieldMapper.select = UIControl.Tql.extend({
            hideLabel: true,
            readOnly: this.transformation !== null
        }).create();
        this.fieldMapper.output = UIControl.StreamSelector.extend({
            typesToShow: [metaStoreService.entities.STREAM, metaStoreService.entities.WACTIONSTORE]
        }).create();
        this.options.StaticControls.lb1 = new UIControl.LineBreak().create();
    },

    postRender: function() {
        if (this.transformation) {
            this.toggleColumnsList(this.input.getValue());
            if (_.isString(this.input.getValue()) && !_.isEmpty(this.input.getValue())) {
                metaStoreService
                    .findById(this.input.getValue())
                    .then(() => {
                        this.originalFields.viewTypeOf(this.input.getValue()).always(() => {
                            this.Form.view.clearDirty(this.originalFields.model);
                        });
                    })
                    .catch((err: Error) => {
                        console.error(err);
                    });
            }
            if (this.transformation?.postRender) {
                this.transformation.postRender(this);
            }
        }
    },

    refreshTql: function(columns?: any[]) {
        const value = columns || this.columns.getValue();
        if (
            !value ||
            value.length === 0 ||
            !_.some(value, function(c) {
                return !!c.column;
            })
        ) {
            this.fieldMapper.select.setValue("");
            return;
        }

        const selectedStreamId = this.input.model.value;
        const stream =
            api.getCurrentNamespace() === metaObjectConverter.getNamespace(selectedStreamId)
                ? metaObjectConverter.getName(selectedStreamId)
                : metaObjectConverter.convertFullNameToShortName(selectedStreamId);
        var tqlBuilder = this.transformation.createRqlBuilder
            ? this.transformation.createRqlBuilder(stream, value, this._options, this.transformation)
            : new TqlBuilder().withStream(stream);

        this.transformation.buildTql(tqlBuilder, value, this._options);

        let tql = tqlBuilder.build();
        this.fieldMapper.select.setValue(tql);
    }
});

export default Module;
